/* eslint-disable react/jsx-wrap-multilines */
import { memo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, DateObject } from 'react-multi-date-picker';

import 'react-multi-date-picker/styles/layouts/prime.css';
import { ArrowLeft, ArrowRight } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { formatDate } from '../../../utils/Date.Util';
import { Icon } from '../Icon.component';
import { Label, LabelType } from '../Label.component';
import { ColorType } from '../shared/Colors.g';
import './Month-Picker.css';

export enum CustomInputType {
  DEFAULT,
  FORMINPUTDATE,
}
export interface InputProps {
  months: Array<Date>;
  format?: string;
  onChange?: (newMonths: any) => void;
  inputBG?: string;
  minDate?: Date;
  maxDate?: Date;
  customInputType?: CustomInputType;
  customFormInputDivWidth?: string;
  customFormInputErrorLabel?: string;
  disabled?: boolean;
}

export interface MonthListPluginProps {
  months: Array<Date>;
}

// custom month list component
const MonthListPlugin = ({ months }: MonthListPluginProps) => {
  const { t } = useTranslation();
  // there is no months selected
  if (!months || months.length === 0) {
    return (
      <div className='w-[224px] flex flex-col gap-2 items-start'>
        <Label text={t('inclued_months')} type={LabelType.LABEL_M_MEDIUM} color={ColorType.BLACK} />
        <Label text={t('no_months')} type={LabelType.BODY3} color={ColorType.GREY4} />
      </div>
    );
  }

  // display month list
  return (
    <div className='h-[280px] flex flex-col gap-2 items-start'>
      <Label
        className='bg-white whitespace-nowrap'
        text={t('inclued_months')}
        type={LabelType.LABEL_M_MEDIUM}
        color={ColorType.BLACK}
      />
      <div className='flex flex-col gap-2 overflow-auto items-start'>
        {months.map((month, index) => {
          return (
            <Label
              key={month ? month.toISOString() : index}
              className=' whitespace-nowrap'
              text={formatDate(month, 'MMMM yyyy')}
              type={LabelType.BODY3}
              color={ColorType.BLACK}
            />
          );
        })}
      </div>
    </div>
  );
};

export const MonthPicker = memo(
  ({
    months,
    onChange,
    format = 'MMM YYYY',
    inputBG = 'bg-grey1 hover:bg-grey2',
    minDate,
    maxDate,
    customInputType = CustomInputType.DEFAULT,
    customFormInputDivWidth = '',
    customFormInputErrorLabel = '',
    disabled = false,
  }: InputProps) => {
    const componentRef = useRef(null);
    const monthShortNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    const onDateChanged = useCallback(
      (dateObjects: DateObject | DateObject[] | null) => {
        let newMonth: Date[] = [];
        if (Array.isArray(dateObjects)) {
          dateObjects.forEach((dateObject) => {
            if (dateObject instanceof DateObject) {
              newMonth.push(dateObject.toDate());
            }
          });
        } else if (dateObjects) {
          newMonth = [dateObjects.toDate()];
        }

        // return new date array directly
        onChange && onChange(newMonth);
      },
      [onChange],
    );

    return (
      <Calendar
        ref={componentRef}
        currentDate={new DateObject({ date: maxDate })}
        renderButton={(direction: 'left' | 'right', handleClick: (e: Event) => void) => {
          return (
            <Icon
              onClick={(e: Event) => {
                handleClick(e);
              }}
              src={direction === 'right' ? ArrowRight : ArrowLeft}
              size={IconSize.SIZE_20x20}
            />
          );
        }}
        value={months}
        onChange={(dateObjects: DateObject | DateObject[] | null) => {
          onDateChanged(dateObjects);
        }}
        minDate={minDate}
        maxDate={maxDate}
        format={format}
        onlyMonthPicker
        multiple
        months={monthShortNames}
        disabled={disabled}
        monthYearSeparator=' '
        formatMonth={() => {
          return '';
        }}
        formatYear={(year) => {
          return year;
        }}
        plugins={[<MonthListPlugin months={months} />]}
      />
    );
  },
);
